import React, { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { Field } from "redux-form";

import {
  Box,
  Button,
  Loader,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { OTPAuthCodeField } from "@js/apps/common/forms/fields/otp/otp-auth-code";
import { QRCode } from "@js/components/qrcode";
import { PasswordField } from "@js/forms/fields/password";
import { required } from "@js/forms/validators";

import styles from "./styles.module.scss";

export type PasskeyConfirmationProps = {
  isConfirmed: boolean;
  isAuthenticating: boolean;
  error: string | null;
  onAuthenticate: () => void;
};

type EnableOTPAuthConentProps = {
  OTPAuthURI: string;
  OTPAuthSecret: string;
  renderPasswordField?: boolean;
  passkeyConfirmation?: PasskeyConfirmationProps;
};

export const EnableOTPContent = ({
  OTPAuthURI,
  OTPAuthSecret,
  renderPasswordField = true,
  passkeyConfirmation,
}: EnableOTPAuthConentProps) => {
  const isMobileDevice = isMobile || isTablet;
  const showFirstStep = renderPasswordField || !!passkeyConfirmation;

  return (
    <ol id="2fa-instructions-list" className={styles.list}>
      {passkeyConfirmation ? (
        <PasskeyConfirmationStep {...passkeyConfirmation} />
      ) : (
        renderPasswordField && (
          <>
            <OtpLiComponent text="Enter the current account password." />
            <OTPConfirmPasswordField />
          </>
        )
      )}
      <Box sx={{ mt: showFirstStep ? 0.5 : 2 }}>
        <OtpLiComponent
          text="Install Authy, Google Authenticator, or another authenticator app of
      your choice."
        />
        {isMobileDevice ? (
          <MobileContent OTPAuthSecret={OTPAuthSecret} />
        ) : (
          <DesktopContent
            OTPAuthURI={OTPAuthURI}
            OTPAuthSecret={OTPAuthSecret}
          />
        )}
        <OtpLiComponent text="Enter the 6-digit code generated by your authenticator app." />
        <OTPAuthCodeField
          name="code"
          inputProps={{
            "aria-describedby": "2fa-instructions-list",
            autoFocus: !showFirstStep,
          }}
        />
      </Box>
    </ol>
  );
};

const DesktopContent = ({
  OTPAuthURI,
  OTPAuthSecret,
}: {
  OTPAuthURI: string;
  OTPAuthSecret: string;
}) => {
  const [showSecret, setShowSecret] = useState(false);

  const handleShowSecret = () => {
    setShowSecret(!showSecret);
  };

  return (
    <>
      <OtpLiComponent text="Open the app and find the “Scan a barcode/QR code” option." />
      <OtpLiComponent text="Scan the QR code below.">
        <br />
        <div className="center">
          <QRCode value={OTPAuthURI} />
          <Typography component="p" variant="paragraph" size="small">
            Unable to scan? You can use the{" "}
            <ClickableText onClick={handleShowSecret}>setup key</ClickableText>{" "}
            to manually configure your authenticator app.
          </Typography>
          {showSecret && (
            <Typography
              component="p"
              variant="paragraph"
              fontWeight={500}
              sx={{ mt: 2 }}
            >
              {OTPAuthSecret}
            </Typography>
          )}
        </div>
      </OtpLiComponent>
    </>
  );
};

const MobileContent = ({ OTPAuthSecret }: { OTPAuthSecret: string }) => {
  return (
    <OtpLiComponent text="Open the app and enter the setup key below.">
      <Typography
        component="p"
        variant="paragraph"
        fontWeight={500}
        sx={{ textAlign: "center", mt: 3 }}
      >
        {OTPAuthSecret}
      </Typography>
    </OtpLiComponent>
  );
};

const OtpLiComponent = ({
  text,
  children,
}: {
  text: string;
  children?: React.ReactNode;
}) => {
  return (
    <li>
      <Typography component="p" variant="paragraph">
        {text}
      </Typography>
      {children && children}
    </li>
  );
};

const PasskeyConfirmationStep = ({
  isConfirmed,
  isAuthenticating,
  error,
  onAuthenticate,
}: PasskeyConfirmationProps) => {
  if (isConfirmed) {
    return <OtpLiComponent text="Identity confirmed with your passkey." />;
  }

  return (
    <OtpLiComponent text="Confirm it’s you using your passkey.">
      <Box sx={{ mt: 1.5, mb: 2 }}>
        {!!error && (
          <Typography
            component="p"
            color="negative"
            fontWeight={500}
            sx={{ mb: 1 }}
          >
            {error}
          </Typography>
        )}
        <Button
          variant="dark-violet"
          shape="squared"
          fullWidth
          onClick={onAuthenticate}
          disabled={isAuthenticating}
          disabledType="no-change"
          endIcon={isAuthenticating ? <Loader size={20} /> : undefined}
        >
          {isAuthenticating ? "Authenticating…" : "Authenticate using passkey"}
        </Button>
      </Box>
    </OtpLiComponent>
  );
};

const OTPConfirmPasswordField = () => {
  return (
    <Box sx={{ minHeight: "80px" }}>
      <Field
        validate={[required]}
        component={PasswordField}
        normalize={(value) => value}
        data-lpignore="true"
        id="current_password"
        name="current_password"
        label=""
        color="secondary"
        variant="outlined"
        autoFocus
        slotProps={{
          htmlInput: {
            "aria-label": "password",
          },
        }}
      />
    </Box>
  );
};

const ClickableText = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <span
      style={{ textDecoration: "underline", cursor: "pointer" }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick();
        }
      }}
    >
      {children}
    </span>
  );
};
